/**
 * Phase 76 — Convenience hooks
 * ─────────────────────────────────────────────────────────────────────────────
 * Thin, ergonomic wrappers around `usePhase76Auth()` for the most common
 * authorization checks. Components should import from here instead of
 * touching the full context shape.
 *
 *   useIsSuperAdmin()                  → boolean
 *   useHasPermission(perm)             → boolean      (active company)
 *   useHasModule(moduleKey)            → boolean      (active company)
 *   useIsCompanyOwnerOrCoOwner(id?)    → boolean      (active or specified)
 *   useMyCompanies()                   → MyCompany[]
 *   useActiveCompany()                 → MyCompany | null
 *   usePhase76Hydrated()               → boolean      (initial pass complete)
 *
 * For asynchronous server-evaluated checks (when local cache might be stale
 * or when you explicitly want a round-trip), use the SDK directly via
 * `phase76` from '@/lib/auth/phase76Client'.
 */

import { usePhase76Auth } from './Phase76AuthContext';
import type {
  Phase76ModuleKey,
  Phase76Permission,
  MyCompany,
} from './phase76Client';

// ─── Super-admin ─────────────────────────────────────────────────────────────

export function useIsSuperAdmin(): boolean {
  return usePhase76Auth().isSuperAdmin;
}

// ─── Permission (active company) ─────────────────────────────────────────────

export function useHasPermission(perm: Phase76Permission | string): boolean {
  const { hasPermissionLocal } = usePhase76Auth();
  return hasPermissionLocal(perm);
}

// ─── Module subscription (active company) ────────────────────────────────────

export function useHasModule(moduleKey: Phase76ModuleKey | string): boolean {
  const { hasModuleLocal } = usePhase76Auth();
  return hasModuleLocal(moduleKey);
}

// ─── Owner / co-owner ────────────────────────────────────────────────────────

export function useIsCompanyOwnerOrCoOwner(companyId?: string): boolean {
  const { isCompanyOwnerOrCoOwnerLocal } = usePhase76Auth();
  return isCompanyOwnerOrCoOwnerLocal(companyId);
}

// ─── Companies list & active company ─────────────────────────────────────────

export function useMyCompanies(): MyCompany[] {
  return usePhase76Auth().companies;
}

export function useActiveCompany(): MyCompany | null {
  return usePhase76Auth().activeCompany;
}

// ─── Hydration state ─────────────────────────────────────────────────────────

export function usePhase76Hydrated(): boolean {
  return usePhase76Auth().hydrated;
}

// ─── Re-exports for ergonomic single-import sites ────────────────────────────

export { usePhase76Auth } from './Phase76AuthContext';
export type {
  Phase76AuthState,
} from './Phase76AuthContext';
export {
  phase76,
  Phase76SdkError,
} from './phase76Client';
export type {
  MyCompany,
  MembershipType,
  Phase76Permission,
  Phase76ModuleKey,
} from './phase76Client';
